import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import StatusBar from '@/designs-dev/device/StatusBar';
import ProviderCard from '@/karehero-app/care/shortlists/ProviderCard';
import ShortlistsEmptyState from '@/karehero-app/care/shortlists/ShortlistsEmptyState';
import {
  type PublishedShortlist,
  type ShortlistProvider,
} from '@/karehero-app/care/shortlists/fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

// What a published shortlist says before anybody is on it. Under the paywall the
// list goes live at the moment the request is sent, so this is the first thing a
// family sees rather than an edge case: providers appear as they confirm.
//
// It names the person rather than the process. "Your care expert" is who the family
// deals with; the shortlist filling up is a consequence of work somebody is doing on
// their behalf, and a blank list with a spinner would say the opposite.
const WAITING = {
  title: 'Waiting to hear back',
  body: 'Your care expert has reached out to care providers for availability. Once they confirm, they will appear here.',
} as const;

// A published shortlist: the providers in the CAM's chosen order, each with
// its status and the CAM's note. Dismissed providers never reach this view.
const ShortlistView = ({
  shortlist,
  onBack,
  onOpenProvider,
  onToggleSecured,
}: {
  shortlist: PublishedShortlist;
  onBack: () => void;
  onOpenProvider: (provider: ShortlistProvider) => void;
  onToggleSecured: (provider: ShortlistProvider) => void;
}) => (
  <ScreenRoot>
    <StatusBar />

    <Box
      sx={{
        px: 2,
        py: 0.5,
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Button
        variant='link'
        ariaLabel='Go back'
        iconLeft='chevron-left'
        onPress={onBack}
      >
        Back
      </Button>
    </Box>

    <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2, pb: 3 }}>
      {/* Content capped + centred so it stays phone/tablet-shaped when the
          screen is viewed wide (unframed, tablet); at 375px it's full width. */}
      <Stack sx={{ mx: 'auto', maxWidth: 720 }} spacing={2}>
        {/* No page header while it is waiting. The list's name and its provider
            count sat above the waiting message and said the same thing twice over
            in a thinner voice: the family has just tapped this list, so they know
            which one they are in, and "0 providers · Updated today" is the very
            reading the message exists to prevent. */}
        {shortlist.providers.length > 0 && (
          <PageHeader
            variant='h2'
            title={shortlist.name}
            description={`${shortlist.providers.length} providers · ${shortlist.updated}`}
          />
        )}
        {/* Nobody on it yet. Sat 80px below the chrome — pt={2} on the scroll
            region plus pt={8} here — which is the same offset the shortlist home
            screen gives its empty message. No spinner and no illustration: the
            sentence is the whole message, and a spinner would promise something
            arriving in seconds. */}
        {shortlist.providers.length === 0 ? (
          <Box sx={{ pt: 8 }}>
            <ShortlistsEmptyState title={WAITING.title} body={WAITING.body} />
          </Box>
        ) : (
          /* Reflowing grid: one column at phone width, two when there's room. */
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            }}
          >
            {shortlist.providers.map((p) => (
              <ProviderCard
                key={p.id}
                provider={p}
                onOpen={onOpenProvider}
                onToggleSecured={onToggleSecured}
              />
            ))}
          </Box>
        )}
      </Stack>
    </Box>
  </ScreenRoot>
);

export default ShortlistView;
